import { gql } from '@apollo/client';
import type { UUID, Vehicle, VehicleDriverAuthorizationMode } from '@cobliteam/common-types';

import type {
  BatchCreateVehicleInput,
  BatchSuggestionsResponse,
  CreateVehicleInput,
  Mutation,
  MutationCreateVehicleArgs,
  UpdateVehicleInput,
  ValidateImportVehicleSpreadsheetInput,
  ValidateImportVehicleSpreadsheetResponse,
  VehicleDriverAuthorization,
  VehicleDto,
  VehiclePastAssociationDto,
} from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';
import type { DeviceVehicle } from '../device/definitions';
import { IFuelToAPIFuel, vehicleSizeToAPISize } from './APITransformer';
import { graphQLToDeviceVehicle, graphQLToVehicle } from './GraphQLTransformers';

class GrahpQLVehicleRequester {
  public getVehicles = (
    withOdometer = false,
    withDisablerStatus = false,
    vehicleGroupsIds?: string[],
    withImmobilizerStatus = false,
    withConsumptionGoal = false
  ): Promise<Vehicle[]> => {
    return graphQLRequester
      .query<{ vehicles: VehicleDto[] }>({
        query: gql`
          query fleetVehicles($groupIds: [String]) {
            vehicles(groupIds: $groupIds) {
              id
              licensePlate
              alias
              fleetId
              tagId
              size
              fuelConsumption
              speedLimitInKmh
              isAllowListEnabled
              isImmobilizerEnabled
              vehicleDriverAuthorizationMode
              ${withImmobilizerStatus ? 'immobilizerStatus' : ''}
              groupPath
              device {
                id
                type
                cobliId
              }
              driver {
                id
                name
                phoneNumbers
              }
              model {
                brand
                name
                year
                version
                isSuggestion
                type
              }
              fuel {
                type
                price
                consumption
              }
              groups {
                id
                name
              }
              tags {
                name
              }
              ${
                withOdometer
                  ? ` odometer {
                        odometerInKm
                        createdOdometerInKm
                        createdAt
                        odometerDateAt
                        vehicleId
                        createdByUserName
                      } `
                  : ''
              }

              ${
                withDisablerStatus
                  ? ` disablerStatus {
                    status
                    lastDisablingAction
                    lastDisablingActionTime
                  } `
                  : ''
              }
              lastLocation {
                isPlugged
                timeInMillis
                cobliIngestedAt
              }
              ${
                withConsumptionGoal
                  ? ` consumptionGoal {
                    vehicleId
                    goal
                    source
                    createdAt
                    updatedAt
                  } `
                  : ''
              }
            }
          }
        `,
        variables: { groupIds: vehicleGroupsIds },
      })
      .then((graphQlResponse) => graphQlResponse.vehicles.map((graphQLVehicle) => graphQLToVehicle(graphQLVehicle)));
  };

  public getVehicle = (vehicleId: string, withOdometer = false, withDisablerStatus = false): Promise<Vehicle> => {
    return graphQLRequester
      .query<{ vehicle: VehicleDto }>({
        query: gql`
          query vehicle($id: ID!) {
            vehicle(id: $id) {
              id
              licensePlate
              alias
              fleetId
              tagId
              size
              fuelConsumption
              speedLimitInKmh
              isAllowListEnabled
              isImmobilizerEnabled
              vehicleDriverAuthorizationMode
              immobilizerStatus
              device {
                id
                type
                cobliId
              }
              driver {
                id
                name
                phoneNumbers
              }
              model {
                brand
                name
                year
                version
                isSuggestion
                type
              }
              fuel {
                type
                price
                consumption
              }
              groups {
                id
                name
              }
              ${
                withOdometer
                  ? ` odometer {
                        odometerInKm
                        createdOdometerInKm
                        createdAt
                        odometerDateAt
                        vehicleId
                        createdByUserName
                      } `
                  : ''
              }
              ${
                withDisablerStatus
                  ? ` disablerStatus {
                    status
                    lastDisablingAction
                    lastDisablingActionTime
                  } `
                  : ''
              }
              lastLocation {
                isPlugged
                timeInMillis
                cobliIngestedAt
              }
              tags {
                name
              }
            }
          }
        `,
        variables: { id: vehicleId },
      })
      .then((graphQlResponse) => graphQLToVehicle(graphQlResponse.vehicle));
  };

  public getVehiclesWithDevices = (vehicleGroupsIds?: string[]): Promise<DeviceVehicle[]> =>
    graphQLRequester
      .query<{ vehicles: VehicleDto[] }>({
        query: gql`
          query fleetVehiclesWithDevices($groupIds: [String]) {
            vehicles(groupIds: $groupIds) {
              id
              licensePlate
              model {
                brand
                name
                type
              }
              device {
                id
                cobliId
              }
            }
          }
        `,
        variables: { groupIds: vehicleGroupsIds },
      })
      .then((graphQlResponse) => graphQlResponse.vehicles.map((graphQLDeviceVehicle) => graphQLToDeviceVehicle(graphQLDeviceVehicle)));

  public getVehiclePastAssociations = (vehicleId: string, referenceTimestamp: string): Promise<VehiclePastAssociationDto[]> =>
    graphQLRequester
      .query<{ vehiclePastAssociations: VehiclePastAssociationDto[] }>({
        query: gql`
            query vehiclePastAssociations($vehicleId: ID!, $referenceTimestamp: String!) {
              vehiclePastAssociations(vehicleId: $vehicleId referenceTimestamp: $referenceTimestamp) {
                identificationMethod
                associatedAt
                driver {
                  id
                  name
                }
                associatedBy {
                  id
                  full_name
                }
              }
            }

            `,
        variables: { vehicleId: vehicleId, referenceTimestamp: referenceTimestamp },
      })
      .then((graphQlResponse) => graphQlResponse.vehiclePastAssociations);

  public saveVehicle = (vehicle: Vehicle): Promise<boolean> => {
    const updateVehicleInput: UpdateVehicleInput = {
      alias: vehicle.alias || null,
      brand: vehicle.model.brand,
      driverId: vehicle.newDriver ? vehicle.newDriver.id : vehicle.driver?.id,
      fuelConsumption: vehicle.fuelConsumption,
      fuelPrice: vehicle.fuel?.price,
      fuelType: vehicle.fuel ? IFuelToAPIFuel(vehicle.fuel) : undefined,
      groupIds: vehicle.groups?.map((group) => group.id),
      licensePlate: vehicle.licensePlate,
      model: vehicle.model.name,
      size: vehicleSizeToAPISize(vehicle.size || ''),
      speedInKmh: vehicle.speedLimitInKmh,
      year: vehicle.model.year ? Number.parseInt(vehicle.model.year) : undefined,
      tagNames: vehicle.tags?.map((tag) => tag.name),
      type: vehicle.model.type,
    };
    return graphQLRequester
      .mutate({
        mutation: gql`
          mutation UpdateVehicle($id: ID!, $input: UpdateVehicleInput!) {
            updateVehicle(id: $id, input: $input) {
              id
            }
          }
        `,
        variables: { id: vehicle.id, input: updateVehicleInput },
      })
      .then(() => true);
  };

  public createVehicle = (vehicle: Vehicle): Promise<UUID> => {
    const createVehicleInput: CreateVehicleInput = {
      alias: vehicle.alias || undefined,
      branding: {
        brand: vehicle.model?.brand,
        model: vehicle.model?.name,
        year: vehicle.model?.year ? Number.parseInt(vehicle.model.year) : undefined,
        type: vehicle.model?.type,
      },
      driverId: vehicle.newDriver ? vehicle.newDriver.id : vehicle.driver?.id,
      fuel: {
        consumption: vehicle.fuelConsumption,
        price: vehicle.fuel?.price,
        type: vehicle.fuel ? IFuelToAPIFuel(vehicle.fuel) : undefined,
      },
      groupIds: vehicle.groups?.map((group) => group.id),
      licensePlate: vehicle.licensePlate || '',
      size: vehicleSizeToAPISize(vehicle.size || ''),
      speedInKmh: vehicle.speedLimitInKmh,
      tagNames: vehicle.tags?.map((tag) => tag.name),
    };
    return graphQLRequester
      .mutate<{ createVehicle: Pick<Mutation['createVehicle'], 'id'> }, MutationCreateVehicleArgs>({
        mutation: gql`
          mutation createVehicle($input: CreateVehicleInput!) {
            createVehicle(input: $input) {
              id
            }
          }
        `,
        variables: { input: createVehicleInput },
      })
      .then((response) => {
        return response.createVehicle.id;
      });
  };

  public getVehicleDriverAuthorization = (vehicleId: string): Promise<VehicleDriverAuthorization> => {
    return graphQLRequester
      .query<{ vehicleDriverAuthorizations: VehicleDriverAuthorization }>({
        query: gql`
        query vehicleDriverAuthorizations($vehicleId: ID!) {
          vehicleDriverAuthorizations(vehicleId: $vehicleId) {
            vehicle {
              id
              licensePlate
              fleetId
              isAllowListEnabled
              isImmobilizerEnabled
              vehicleDriverAuthorizationMode
              immobilizerStatus
              model {
                brand
                name
                year
                version
                isSuggestion
                type
              }
            }
            authorizations {
              authorizationId
              device {
                id
                type
                cobliId
              }
              driver {
                id
                name
              }
              identificationToken {
                token_id
                token_type
                token_alias
                status
                is_external
              }
            }
          }
        }
      `,
        variables: { vehicleId },
      })
      .then((response) => response.vehicleDriverAuthorizations);
  };

  public updateVehicleDriverAuthorizations = (
    vehicleId: string,
    driverIds: string[],
    isImmobilizerEnabled: boolean,
    vehicleDriverAuthorizationMode: VehicleDriverAuthorizationMode
  ): Promise<VehicleDto> => {
    return graphQLRequester
      .mutate<{ updateVehicleImmobilizer: VehicleDto }>({
        mutation: gql`
        mutation updateVehicleImmobilizer($input: UpdateVehicleImmobilizerInput!) {
          updateVehicleImmobilizer(input: $input) {
            id
            isImmobilizerEnabled
            isAllowListEnabled
          }
        }
      `,
        variables: { input: { id: vehicleId, isImmobilizerEnabled, vehicleDriverAuthorizationMode, driverIds } },
      })
      .then((response) => response.updateVehicleImmobilizer);
  };

  public validateImportVehicleSpreadsheet = (input: ValidateImportVehicleSpreadsheetInput): Promise<ValidateImportVehicleSpreadsheetResponse> => {
    return graphQLRequester
      .mutate<{ validateImportVehicleSpreadsheet: ValidateImportVehicleSpreadsheetResponse }>({
        mutation: gql`
          mutation ValidateImportVehicleSpreadsheet($input: ValidateImportVehicleSpreadsheetInput!) {
            validateImportVehicleSpreadsheet(input: $input) {
              validRows {
                licensePlate
                fuelType
                fuelPrice
                fuelConsumption
                size
                speedLimitInKmh
              }
              invalidRows {
                data {
                  rowIndex
                  licensePlate
                  fuelType
                  fuelPrice
                  fuelConsumption
                  size
                  speedLimitInKmh
                }
                errors {
                  field
                  message
                }
              }
            }
          }
        `,
        variables: { input },
      })
      .then((response) => response.validateImportVehicleSpreadsheet);
  };

  public getVehicleSuggestionsBatch = (plates: string[]): Promise<BatchSuggestionsResponse> => {
    return graphQLRequester
      .query<{ vehicleSuggestionsBatch: BatchSuggestionsResponse }>({
        query: gql`
          query VehicleSuggestionsBatch($input: BatchSuggestionInput!) {
            vehicleSuggestionsBatch(input: $input) {
              results {
                key
                value {
                  make
                  model
                  year
                  plate
                  vin
                }
              }
            }
          }
        `,
        variables: { input: { plates } },
      })
      .then((response) => response.vehicleSuggestionsBatch);
  };

  public createVehiclesBatch = (vehicles: BatchCreateVehicleInput[]): Promise<VehicleDto[]> => {
    return graphQLRequester
      .mutate<{ createVehiclesBatch: VehicleDto[] }>({
        mutation: gql`
          mutation CreateVehiclesBatch($input: CreateVehiclesBatchInput!) {
            createVehiclesBatch(input: $input) {
              id
              licensePlate
            }
          }
        `,
        variables: { input: { vehicles } },
      })
      .then((response) => response.createVehiclesBatch);
  };
}

export default GrahpQLVehicleRequester;
